import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Users,
  Loader2,
  Ban,
  CircleCheck,
  Trash2,
  Search,
  Mail,
  Coins,
  ShieldAlert,
  RefreshCw,
} from "lucide-react";
import { api, formatApiErrorDetail } from "../../lib/api";

/**
 * Admin-only user management panel.
 * - Lists every registered account (newest first).
 * - Block / unblock an account (blocked users can't log in).
 * - Permanently delete an account and every artifact it owns.
 *
 * Mounted inside /profile when `user.is_admin === true`.
 */
export default function AdminUsersPanel({ currentAdminId }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [filter, setFilter] = useState("");
  const [busyId, setBusyId] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null); // user object or null

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get("/admin/users");
      setUsers(data.users || []);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Could not load users.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggleBlock = async (u) => {
    setBusyId(u.id);
    try {
      const { data } = await api.patch(`/admin/users/${u.id}`, { blocked: !u.blocked });
      setUsers((prev) => prev.map((x) => (x.id === u.id ? { ...x, blocked: data.blocked } : x)));
      toast.success(data.blocked ? "User blocked" : "User unblocked");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Action failed.");
    } finally {
      setBusyId(null);
    }
  };

  const doDelete = async (u) => {
    setBusyId(u.id);
    try {
      await api.delete(`/admin/users/${u.id}`);
      setUsers((prev) => prev.filter((x) => x.id !== u.id));
      setConfirmDelete(null);
      toast.success(`Deleted ${u.email}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Delete failed.");
    } finally {
      setBusyId(null);
    }
  };

  const q = filter.trim().toLowerCase();
  const filtered = q
    ? users.filter((u) => (u.email || "").toLowerCase().includes(q) || (u.name || "").toLowerCase().includes(q))
    : users;

  const fmtDate = (iso) => {
    if (!iso) return "—";
    try {
      return new Date(iso).toLocaleString(undefined, {
        year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
      });
    } catch { return "—"; }
  };

  return (
    <div data-testid="admin-users-panel">
      <div className="flex items-center gap-2 mb-2">
        <Users className="w-4 h-4 text-[#F5C542]" />
        <h2 className="label-eyebrow text-[#F5C542]">Admin — Users</h2>
      </div>
      <p className="text-[13px] text-white/60 mb-5 max-w-lg">
        See every registered account, block anyone who shouldn't be able to sign in, or permanently delete an account and all of its data.
      </p>

      <div className="flex items-center gap-3 mb-4">
        <div className="relative flex-1 max-w-xs">
          <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-white/40 pointer-events-none" />
          <input
            type="search"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Filter by email or name…"
            className="cmd-input w-full h-9 pl-9 pr-3 rounded-md text-[13px]"
            data-testid="admin-users-filter"
          />
        </div>
        <button
          type="button"
          onClick={load}
          disabled={loading}
          className="btn-ghost focus-ring"
          data-testid="admin-users-refresh"
        >
          {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
          <span className="text-[11px] uppercase tracking-[0.15em]">Refresh</span>
        </button>
        <span className="ml-auto label-eyebrow opacity-60" data-testid="admin-users-count">
          {filtered.length} / {users.length}
        </span>
      </div>

      {error && (
        <div
          className="text-[12px] font-mono-p text-[#ff9c85] border border-[#ff9c85]/30 rounded px-3 py-2 bg-[#ff9c85]/5 mb-4"
          data-testid="admin-users-error"
        >
          {error}
        </div>
      )}

      <div className="space-y-2">
        {filtered.length === 0 && !loading && (
          <div className="p-6 text-center label-eyebrow opacity-60 border border-white/10 rounded-md">
            No users match
          </div>
        )}
        {filtered.map((u) => {
          const isSelf = u.id === currentAdminId;
          const isAdminRow = u.is_admin;
          const protected_ = isSelf || isAdminRow;
          return (
            <div
              key={u.id}
              className={`p-3 rounded-md border transition-colors ${
                u.blocked
                  ? "border-[#ff9c85]/40 bg-[#ff9c85]/[0.04]"
                  : isAdminRow
                  ? "border-[#F5C542]/30 bg-[#F5C542]/[0.03]"
                  : "border-white/10 bg-white/[0.02]"
              }`}
              data-testid={`admin-user-row-${u.id}`}
            >
              <div className="flex items-center gap-3">
                {u.picture ? (
                  <img src={u.picture} alt="" className="w-9 h-9 rounded-full object-cover border border-white/10 flex-shrink-0" />
                ) : (
                  <div className="w-9 h-9 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-[13px] font-display font-black flex-shrink-0">
                    {((u.name || u.email || "?")[0] || "?").toUpperCase()}
                  </div>
                )}

                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[13px] text-white/95 font-medium truncate" data-testid={`admin-user-email-${u.id}`}>
                      {u.email}
                    </span>
                    {isAdminRow && (
                      <span className="label-eyebrow text-[#F5C542] flex items-center gap-1">
                        <ShieldAlert className="w-3 h-3" /> admin
                      </span>
                    )}
                    {u.blocked && (
                      <span className="label-eyebrow text-[#ff9c85]" data-testid={`admin-user-blocked-${u.id}`}>
                        blocked
                      </span>
                    )}
                    {u.auth_provider === "google" && (
                      <span className="label-eyebrow opacity-60">google</span>
                    )}
                  </div>
                  <div className="flex items-center gap-3 mt-0.5 text-[11px] text-white/50 font-mono-p">
                    {u.name && <span className="truncate">{u.name}</span>}
                    <span className="flex items-center gap-1"><Coins className="w-3 h-3" />{u.credits}</span>
                    <span>{u.search_count} searches</span>
                    <span className="hidden sm:inline">joined {fmtDate(u.created_at)}</span>
                  </div>
                </div>

                <div className="flex items-center gap-1 flex-shrink-0">
                  <a
                    href={`mailto:${u.email}`}
                    className="btn-ghost !h-8 !px-2 focus-ring hidden sm:inline-flex"
                    aria-label="Email user"
                    title="Email user"
                  >
                    <Mail className="w-3.5 h-3.5 text-white/60" />
                  </a>
                  <button
                    type="button"
                    onClick={() => toggleBlock(u)}
                    disabled={protected_ || busyId === u.id}
                    className="btn-ghost !h-8 !px-3 focus-ring disabled:opacity-30"
                    title={isAdminRow ? "The admin account cannot be blocked" : u.blocked ? "Unblock user" : "Block user"}
                    data-testid={`admin-user-block-${u.id}`}
                  >
                    {busyId === u.id ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : u.blocked ? (
                      <CircleCheck className="w-3.5 h-3.5 text-emerald-300" />
                    ) : (
                      <Ban className="w-3.5 h-3.5 text-[#ffb097]" />
                    )}
                    <span className="text-[11px] uppercase tracking-[0.15em]">
                      {u.blocked ? "Unblock" : "Block"}
                    </span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setConfirmDelete(u)}
                    disabled={protected_ || busyId === u.id}
                    className="btn-ghost !h-8 !px-2 focus-ring disabled:opacity-30"
                    title={protected_ ? "The admin account cannot be deleted" : "Delete user"}
                    aria-label="Delete user"
                    data-testid={`admin-user-delete-${u.id}`}
                  >
                    <Trash2 className="w-3.5 h-3.5 text-[#ff9c85]" />
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {confirmDelete && (
        <div
          className="fixed inset-0 z-[80] bg-black/80 backdrop-blur-md flex items-center justify-center p-4"
          data-testid="admin-user-delete-modal"
          onClick={(e) => { if (e.target === e.currentTarget) setConfirmDelete(null); }}
        >
          <div className="w-full max-w-md rounded-lg border border-white/10 bg-[#0F0F0F] shadow-2xl p-6">
            <div className="label-eyebrow text-[#ff9c85] mb-2">permanent deletion</div>
            <h3 className="font-display text-lg font-black tracking-tight mb-3">Delete {confirmDelete.email}?</h3>
            <p className="text-[13px] text-white/70 leading-relaxed mb-5">
              This removes the account and every search, pin, session, upload, and payment record it owns. This cannot be undone.
            </p>
            <div className="flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => setConfirmDelete(null)}
                className="btn-ghost focus-ring"
                data-testid="admin-user-delete-cancel"
                disabled={busyId === confirmDelete.id}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => doDelete(confirmDelete)}
                disabled={busyId === confirmDelete.id}
                className="btn-pill focus-ring !bg-[#ff9c85]/15 !border-[#ff9c85]/40 !text-[#ffb097]"
                data-testid="admin-user-delete-confirm"
              >
                {busyId === confirmDelete.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                <span>Permanently delete</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
